import { Component, Input, Output, EventEmitter, forwardRef, AfterViewInit, OnDestroy, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Wrapper component for ap-form-field with type="inputNumber" that allows null values.
 * 
 * This component wraps the form-field control and overrides its behavior
 * to allow clearing the input (null values) instead of forcing it to minNum.
 * 
 * Usage - replace ap-form-field with gtw-nullable-number-input:
 * 
 * OLD:
 * <ap-form-field 
 *   type="inputNumber" 
 *   [minNum]="0" 
 *   [maxNum]="100"
 *   [(value)]="myValue">
 * </ap-form-field>
 * 
 * NEW:
 * <gtw-nullable-number-input 
 *   [minNum]="0" 
 *   [maxNum]="100"
 *   [(ngModel)]="myValue">
 * </gtw-nullable-number-input>
 * 
 * OR with all original properties:
 * <gtw-nullable-number-input
 *   [label]="'My Label'"
 *   [id]="'myInput'"
 *   [minNum]="0"
 *   [maxNum]="100"
 *   [funType]="'typeTwo'"
 *   [isDisabled]="false"
 *   [(ngModel)]="myValue">
 * </gtw-nullable-number-input>
 */
@Component({
    selector: 'gtw-nullable-number-input',
    templateUrl: './nullable-number-input.component.html',
    styleUrls: ['./nullable-number-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NullableNumberInputComponent),
            multi: true
        }
    ],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NullableNumberInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() inlineBlock: boolean = false;
  @Input() placeholder: string = '';
  @Input() isDisabled: boolean = false;
  @Input() isSecondary: boolean = false;
  @Input() isLargeInput: boolean = false;
  @Input() minNum?: number;
  @Input() maxNum?: number;
  
  @Output() onChangeEvent = new EventEmitter<any>();
  
  @ViewChild('formField', { read: ElementRef }) formFieldRef?: ElementRef;

  internalValue: number | null = null;
  displayValue: string = '';
  
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private inputElement?: HTMLInputElement;
  
  // Bound event handlers for proper cleanup
  private boundHandleKeyDown = this.handleKeyDown.bind(this);
  private boundHandleInput = this.handleInput.bind(this);
  private boundHandleBlur = this.handleBlur.bind(this);

  ngAfterViewInit(): void {
    // Find the native input element inside ap-form-field and add listeners
    setTimeout(() => {
      if (this.formFieldRef) {
        const nativeEl = this.formFieldRef.nativeElement as HTMLElement;
        const inputElement = nativeEl.tagName === 'INPUT'
          ? nativeEl as HTMLInputElement
          : nativeEl.querySelector('input') as HTMLInputElement;
        if (inputElement) {
          this.inputElement = inputElement;
          this.inputElement?.addEventListener('keydown', this.boundHandleKeyDown);
          this.inputElement?.addEventListener('input', this.boundHandleInput);
          this.inputElement?.addEventListener('blur', this.boundHandleBlur);
        }
      }
    }, 0);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    
    // Allow: backspace, delete, tab, escape, enter
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(key)) {
      return;
    }
    
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    
    // Allow: home, end, left, right, up, down
    if (['Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) {
      return;
    }
    
    const currentValue = (event.target as HTMLInputElement).value;
    const cursorPosition = (event.target as HTMLInputElement).selectionStart || 0;
    
    // Allow minus only at the beginning and only if minNum allows negative numbers
    if (key === '-') {
      if (this.minNum !== undefined && this.minNum >= 0) {
        event.preventDefault();
        return;
      }
      if (cursorPosition !== 0 || currentValue.includes('-')) {
        event.preventDefault();
        return;
      }
      return; // Allow
    }
    
    // Allow decimal point (only one)
    if (key === '.' || key === ',') {
      if (currentValue.includes('.') || currentValue.includes(',')) {
        event.preventDefault();
        return;
      }
      return; // Allow
    }
    
    // Allow digits 0-9
    if (key >= '0' && key <= '9') {
      return; // Allow
    }
    
    // Block everything else
    event.preventDefault();
  }

  private handleInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;
    
    // Don't validate empty/incomplete values during typing
    if (value === '' || value === '-' || value === '.' || value === '-.') {
      this.displayValue = value;
      return;
    }
    
    // Clean the value
    let cleanValue = value.replace(/[^0-9.-]/g, '');
    
    // Ensure only one minus at the beginning
    if (cleanValue.indexOf('-') > 0) {
      cleanValue = cleanValue.replace(/-/g, '');
    } else if (cleanValue.lastIndexOf('-') > 0) {
      cleanValue = '-' + cleanValue.replace(/-/g, '');
    }
    
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Parse to number
    const numValue = parseFloat(cleanValue);
    
    if (!isNaN(numValue)) {
      // Check max limit
      if (this.maxNum !== undefined && numValue > this.maxNum) {
        cleanValue = this.maxNum.toString();
        inputElement.value = cleanValue;
      }
      
      // Check min limit (but allow typing negative numbers)
      if (this.minNum !== undefined && numValue < this.minNum && !cleanValue.endsWith('.')) {
        // Don't enforce min while user is still typing (e.g., typing "10" but first types "1")
        // Only enforce if the value is complete (doesn't end with decimal point)
        const isNegativeInProgress = cleanValue === '-' || (cleanValue.startsWith('-') && cleanValue.length <= 2);
        if (!isNegativeInProgress && numValue < this.minNum) {
          cleanValue = this.minNum.toString();
          inputElement.value = cleanValue;
        }
      }
    }
    
    // Update displayValue if it changed
    if (cleanValue !== value) {
      inputElement.value = cleanValue;
    }
    
    this.displayValue = inputElement.value;
  }

  private handleBlur(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;
    
    // Handle empty value
    if (value === '' || value === '-' || value === '.') {
      this.internalValue = null;
      this.displayValue = '';
      inputElement.value = '';
      this.onChange(null);
      this.onTouched();
      return;
    }
    
    // Clean and validate
    let cleanValue = value.replace(/[^0-9.-]/g, '');
    const numValue = parseFloat(cleanValue);
    
    if (isNaN(numValue)) {
      this.internalValue = null;
      this.displayValue = '';
      inputElement.value = '';
      this.onChange(null);
    } else {
      // Enforce min/max on blur
      let finalValue = numValue;
      
      if (this.minNum !== undefined && finalValue < this.minNum) {
        finalValue = this.minNum;
      }
      
      if (this.maxNum !== undefined && finalValue > this.maxNum) {
        finalValue = this.maxNum;
      }
      
      this.internalValue = finalValue;
      this.displayValue = finalValue.toString();
      inputElement.value = finalValue.toString();
      this.onChange(finalValue);
    }
    
    this.onTouched();
  }

  handleChange(event: any): void {
    
    let value = event.value;
    
    // Handle empty/null values - DON'T force to minNum
    if (value === null || value === undefined || value === '' || 
        (typeof value === 'string' && value.trim() === '')) {
      this.internalValue = null;
      this.displayValue = '';
      this.onChange(null);
      this.onTouched();
      this.onChangeEvent.emit({ event: event.event, value: null });
      return;
    }
    
    // Remove any non-numeric characters except minus and decimal point
    // Allow only: digits, one minus at start, one decimal point
    let cleanValue = value.toString();
    
    // Remove all characters except digits, minus, and decimal point
    cleanValue = cleanValue.replace(/[^0-9.-]/g, '');
    
    // Ensure only one minus at the beginning
    if (cleanValue.indexOf('-') > 0) {
      cleanValue = cleanValue.replace(/-/g, '');
    } else if (cleanValue.lastIndexOf('-') > 0) {
      cleanValue = '-' + cleanValue.replace(/-/g, '');
    }
    
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // If value was cleaned (had invalid characters), update displayValue immediately
    if (cleanValue !== value.toString()) {
      this.displayValue = cleanValue;
    }
    
    // Handle numeric values with range validation
    const numValue = parseFloat(cleanValue);
    
    if (isNaN(numValue) || cleanValue === '' || cleanValue === '-' || cleanValue === '.') {
      this.internalValue = null;
      this.displayValue = '';
      this.onChange(null);
    } else if (this.minNum !== undefined && numValue < this.minNum) {
      this.internalValue = this.minNum;
      this.displayValue = this.minNum.toString();
      this.onChange(this.minNum);
    } else if (this.maxNum !== undefined && numValue > this.maxNum) {
      this.internalValue = this.maxNum;
      this.displayValue = this.maxNum.toString();
      this.onChange(this.maxNum);
    } else {
      this.internalValue = numValue;
      this.displayValue = numValue.toString();
      this.onChange(numValue);
    }
    
    this.onTouched();
    this.onChangeEvent.emit({ event: event.event, value: this.internalValue });
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    
    // Allow null values - don't convert to minNum
    if (value === null || value === undefined || value === '') {
      this.internalValue = null;
      this.displayValue = '';
    } else {
      this.internalValue = value;
      this.displayValue = value.toString().replace(/,/g, '');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    if (this.inputElement) {
      this.inputElement.removeEventListener('keydown', this.boundHandleKeyDown);
      this.inputElement.removeEventListener('input', this.boundHandleInput);
      this.inputElement.removeEventListener('blur', this.boundHandleBlur);
    }
  }
}

